import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, signal, ViewChild, WritableSignal } from '@angular/core';
import { AuthenticationService } from '../../core/authentication.service';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { FeedbackPopupComponent } from '../global/feedback-popup/feedback-popup.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { HotkeysService } from '../global/hotkeys/hotkeys.service';
import { getFromLocalStorage, hasViewPermissionForPath, isChristmasSeason, sendToGoogleAnalytics, setToLocalStorage } from '../../core/utilities';
import { FeedbackService } from '../../core/feedback.service';
import { NavigationService } from '../../core/navigation.service';
import { delay, filter, map, skip, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { BehaviorSubject, combineLatestWith, EMPTY, Observable, Subject } from 'rxjs';
import { MatMenu, MatMenuContent, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MainRoutes } from '../../core/enums';
import { Store } from '@ngrx/store';
import { selectAccount, selectAccountAiInsightsEnabled, selectAccounts, selectJasonerEnabled, selectUser, selectUserDetails, selectUserGxWrapped, selectUserStatus } from '../../store/global/core-selectors';
import { Auth0User } from '../../models/auth0User';
import { ApiService } from '../../core/api-service';
import { LANGUAGES } from '../../core/constants';
import { CoreUserActions, updateSelectedAccount } from '../../store/global/core-actions';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UserStatus } from '../users/enums/user-status-enum';
import { Account } from '../../models/account';
import { UserDetails } from '../../models/users-models/user-details';
import { ConsentService } from '../global/consent/consent.service';
import { environment } from '../../../environments/environment';
import { GtagEventDirective } from '../../core/gx-gtag/gtag-event.directive';
import { MatDivider } from '@angular/material/divider';
import { ClickOutsideModule } from 'ng-click-outside';
import { MatIcon } from '@angular/material/icon';
import { NgClass } from '@angular/common';
import { LetDirective, PushPipe } from '@ngrx/component';
import { MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { GxWrappedPopupComponent } from './gx-wrapped-popup/gx-wrapped-popup.component';
import { LocalStorageKeys } from '../locations-skeleton/enums/local-storage-keys';
import { NotificationsPopupComponent } from './notifications-popup/notifications-popup.component';
import { EmailNotificationSettings } from '../../models/email-notification-settings';
import { HttpErrorResponse } from '@angular/common/http';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { selectQueryParams } from '../../store/global/router-selectors';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { MatBadge } from '@angular/material/badge';
import { AiInsightsTourService } from '../ai-insights/ai-insights-tour.service';
import { LandingPagesTourService } from '../../core/landing-pages-tour.service';
import { RebrandingTourService } from '../../core/rebranding-tour.service';
import { FlagEmojiPipe } from '../../pipes/flag-emoji.pipe';
import { LanguageNativeNamePipe } from '../../pipes/language-native-name.pipe';

@Component({
  selector: 'app-navigation',
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.scss'],
  // Prevent overused change detection leading to many auth service `isLogged()` calls.
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    LetDirective,
    NgClass,
    MatIcon,
    MatMenuTrigger,
    ClickOutsideModule,
    MatMenu,
    MatDivider,
    MatMenuItem,
    GtagEventDirective,
    RouterLink,
    MatMenuContent,
    TranslateModule,
    PushPipe,
    MatIconButton,
    MatTooltip,
    MatProgressSpinner,
    MatBadge,
    FlagEmojiPipe,
    LanguageNativeNamePipe,
  ],
})
export class NavigationComponent implements OnInit, OnDestroy {

  navigationButtons = [
    { route: MainRoutes.HOME, svgName: 'home' },
    { route: MainRoutes.LOCATIONS, svgName: 'location' },
    { route: MainRoutes.REVIEWS, svgName: 'reviews' },
    { route: MainRoutes.INSIGHTS, svgName: 'analytics' },
    { route: MainRoutes.AI_INSIGHTS, svgName: 'ai_generate' },
    { route: MainRoutes.USERS, svgName: 'people' },
    { route: MainRoutes.POSTS, svgName: 'posts' },
  ];
  protected feedbackDialogOpen = false;
  protected activeRoute: string;

  @ViewChild('insightsMenuTrigger', { read: MatMenuTrigger }) insightsMenuTrigger: MatMenuTrigger;
  @ViewChild('insightsMenuTrigger', { read: ElementRef }) insightsMenuTriggerElement: ElementRef;
  @ViewChild('accountSettingsMenuTrigger', { read: MatMenuTrigger }) accountSettingsMenuTrigger: MatMenuTrigger;

  private timedInsightsMenuCloser: NodeJS.Timeout;
  private emailSettingsLoading = new BehaviorSubject(false);
  private readonly emailSettingsDialogRef: Subject<MatDialogRef<NotificationsPopupComponent>> = new Subject();

  // HTML Access
  protected readonly isProd = environment.production;
  protected readonly mainRoutes = MainRoutes;
  protected readonly languages = LANGUAGES;
  protected readonly environment = environment;
  protected readonly jasonerLinkLoading: WritableSignal<boolean> = signal(false);
  protected readonly viewModel$: Observable<{
    user: Auth0User,
    details: UserDetails
    accounts: Account[],
    selectedAccount: Account,
    userStatus: UserStatus,
    gxWrapped: string[],
    emailSettingsLoading: boolean
  }> = this.store.select(selectUser)
      .pipe(
          combineLatestWith(this.store.select(selectUserDetails),
              this.store.select(selectAccount),
              this.store.select(selectAccounts),
              this.store.select(selectUserStatus),
              this.store.select(selectUserGxWrapped),
              this.store.select(selectJasonerEnabled),
              this.emailSettingsLoading,
          ),
          map(([user,
                 details,
                 selectedAccount,
                 accounts,
                 userStatus,
                 gxWrapped,
                 jasonerEnabled,
                 emailSettingsLoading]) => ({
            user,
            details,
            selectedAccount,
            accounts,
            userStatus,
            gxWrapped,
            jasonerEnabled,
            emailSettingsLoading,
          })),
      );

  /**
   * Checks if the user is logged in.
   */
  get isLoggedIn$(): Observable<boolean> {
    return this.authService.isAuthenticated$;
  }

  private componentDestroyed = new Subject<void>();

  constructor(
      private authService: AuthenticationService,
      public router: Router,
      private dialog: MatDialog,
      private feedbackService: FeedbackService,
      public hotkeysService: HotkeysService,
      private navigationService: NavigationService,
      private cd: ChangeDetectorRef,
      private store: Store,
      private apiService: ApiService,
      public translate: TranslateService,
      private consentService: ConsentService,
      private aiInsightsTour: AiInsightsTourService,
      private landingPagesTour: LandingPagesTourService,
      private rebrandingTour: RebrandingTourService,
  ) {
    this.router.events.pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.componentDestroyed)).subscribe(() => {
      this.activeRoute = this.router.routerState?.snapshot?.root?.firstChild?.url[0]?.path;
      this.cd.markForCheck();
    });
  }

  ngOnInit(): void {
    // the first navigation end event might come in faster than the listener is registered, so
    // make sure active tab is set
    if (!this.activeRoute) {
      setTimeout(() => {
        this.activeRoute = this.router.routerState?.snapshot?.root?.firstChild?.url[0]?.path;
        this.cd.markForCheck();
      }, 500);
    }

    if (environment.activeFeatures.gxWrapped &&
        !getFromLocalStorage(LocalStorageKeys.GX_WRAPPED)) {
      this.store.select(selectUserGxWrapped)
          .pipe(
              skip(1), // skip the initial undefined
              take(1))
          .subscribe((links) => {
            if (links?.length) {
              this.openGxWrapped(links);
            }
          });
    }

    if (environment.activeFeatures.aiInsightsTour) {
      this.aiInsightsTour.setUpAiInsightsTour(this.store.select(selectAccountAiInsightsEnabled).pipe(delay(500)));
    }

    // Set up landing pages tour - should appear after the aiInsightsTour
    if (environment.activeFeatures.landingPages) {
      this.landingPagesTour.setUpLandingPagesTour(this.store.select(selectUserDetails).pipe(delay(1000)));
    }

    // Set up rebranding tour
    if (environment.activeFeatures.rebrandingTour) {
      this.rebrandingTour.setUpRebrandingTour();
    }

    // When USER wants to unsub from notifications by clicking on the 'unsubscribe' button in
    // their notification email, they should be redirected to LSC with the query the param
    // ?openEmailNotificationSettings=true which should open the popup
    this.store.select(selectQueryParams).pipe(
        filter((queryParams) => !!queryParams), // filter out undefined
        take(1),
    ).subscribe((queryParams) => {
      if (queryParams.openEmailNotificationSettings && // guard for empty string
          coerceBooleanProperty(queryParams.openEmailNotificationSettings)) {
        this.openNotificationSettings();
      }
    });

  }

  ngOnDestroy() {
    this.componentDestroyed.next();
  }

  /**
   * Triggers authentication for the MyBusiness API, Drive API and Sheets API if not authenticated yet.
   */
  startGoogleAuthentication(accountId: number) {
    this.apiService.triggerLocalOAuthProcess(accountId).subscribe(() => {

      this.feedbackService.showSuccessSnackbar({
        data: {
          userFriendlyMessage: 'Google authentication was successful',
        }, duration: 5000,
      });
    });
  }

  /**
   * Logs out the user and redirects to the login page.
   */
  logout() {
    this.authService.logout();
  }

  /**
   * Sets user language with for the UI and saves this choice in the DB and locally in browser storage
   * @param language New language
   */
  setAndUpdateLanguage(language: string): void {
    this.store.dispatch(CoreUserActions.updateUserLanguage({ languageShort: language }));
  }

  /**
   * Returns the 'Places' icon name. Depending on the season, there might be an adapted icon to show.
   */
  getLogoName() {
    if (isChristmasSeason()) {
      return 'places_logo'; // TODO: add christmas logo
    }
    return 'places_logo';
  }

  /**
   * Opens a popup for a feedback survey.
   */
  openFeedbackDialog() {
    this.feedbackDialogOpen = true;
    this.dialog
        .open(FeedbackPopupComponent, {
          width: '400px',
          autoFocus: false,
        }).afterClosed().subscribe(() => this.feedbackDialogOpen = false);
  }

  closeAccountSettingsMenu() {
    this.accountSettingsMenuTrigger.closeMenu();
  }

  openAccountSettingsMenu() {
    this.accountSettingsMenuTrigger.openMenu();
  }

  navigate(route: string) {
    if (this.activeRoute === route) {
      return;
    }
    this.navigationService.navigate([route])
        .then();
  }

  // The following 2 methods make sure that Insight Menu opens on hover and closes if neither
  // the trigger nor the menu are hovered

  /**
   * Opens Insight menu and clears timedInisghtsMenuCloser if present
   */
  openInsightsMenu() {
    if (this.timedInsightsMenuCloser) {
      clearTimeout(this.timedInsightsMenuCloser);
    }
    this.insightsMenuTrigger?.openMenu();
  }

  /**
   * sets up timedInisghtsMenuCloser to execute the registered callback to close Insights menu
   */
  closeInsightsMenu() {
    this.timedInsightsMenuCloser = setTimeout(() => {
      this.insightsMenuTrigger?.closeMenu();
      this.insightsMenuTriggerElement?.nativeElement?.blur();
    }, 200);
  }

  /**
   * Decides which tabs are going to be shown to the user
   * @param userDetails
   * @param tab = current path
   * @param acc
   */
  isTabVisible(userDetails: UserDetails, tab: string, acc: Account) {
    if (tab === MainRoutes.HOME) { // all tabs except HOME are permission-protected
      return true;
    } else {
      return hasViewPermissionForPath(userDetails, tab, acc);
    }
  }

  /**
   * Select a different account
   * @param account
   * @param currentAccountName
   */
  selectAccount(account: Account, currentAccountName: string) {
    if (account.accountName === currentAccountName) {
      return;
    }

    sendToGoogleAnalytics('User Management',
        'Switch account',
        'Switched account to ' + account.accountName);

    this.store.dispatch(updateSelectedAccount({ selectedAccount: account }));
  }

  /**
   * @param status
   * fixes BLS-4918
   */
  isSharedUser(status: UserStatus) {
    return status === UserStatus.SHARED_USER;
  }

  /**
   * Opens GDPR consent popup
   */
  openConsentPopup() {
    this.consentService.openPopup();
  }

  /**
   * Opens a new tab with Jira service desk
   */
  openJira() {
    window.open(' https://gxperts.atlassian.net/servicedesk', '_blank');
  }

  /**
   * Open GX Wrapped Dialog
   */
  openGxWrapped(gxWrappedSlideLinks: string[]) {
    this.dialog.open(GxWrappedPopupComponent, {
      data: gxWrappedSlideLinks,
      disableClose: true,
      // autofocus the close button, otherwise the scroll position changes to the last slide w/ the
      // feedback form which is autofocused
      // https://material.angular.io/components/dialog/overview#focus-management
      autoFocus: 'buttonClose',
    }).afterClosed().subscribe(() => {
      setToLocalStorage(LocalStorageKeys.GX_WRAPPED, true);
    });
  }

  openNotificationSettings(): void {
    let _notificationsSettings: EmailNotificationSettings;
    this.emailSettingsLoading.next(true);

    this.apiService.getEmailNotificationSettings().pipe(
        tap((notificationsSettings) => {
          _notificationsSettings = notificationsSettings.result;
          this.emailSettingsLoading.next(false);
        }),
        switchMap((notificationsSettings) => {
          const dialogRef = this.dialog.open(NotificationsPopupComponent, {
            disableClose: true,
            data: notificationsSettings.result,
          });
          this.emailSettingsDialogRef.next(dialogRef);

          return dialogRef.afterClosed();
        }),
        switchMap((editedNotificationsSettings: EmailNotificationSettings) => {
          if (editedNotificationsSettings.notificationsDailyEnabled !== _notificationsSettings.notificationsDailyEnabled) {
            this.emailSettingsLoading.next(true);
            return this.apiService.setEmailNotificationSettings(editedNotificationsSettings.notificationsDailyEnabled);
          } else return EMPTY;
        }),
        takeUntil(this.componentDestroyed),
    ).subscribe({
      next: (res) => {
        this.emailSettingsLoading.next(false);
        if (res?.result) {
          this.feedbackService.showSuccessSnackbar({
            data: {
              userFriendlyMessage: this.translate.instant('NOTIFICATION_SETTINGS.CHANGE_SUCCESS'),
            },
          });
        }
      }, error: (err: HttpErrorResponse) => {
        this.emailSettingsLoading.next(false);
        console.error(err);
        this.feedbackService.showAlertSnackbar({
          data: {
            userFriendlyMessage: this.translate.instant('ERROR'),
            detailedInfoFromBE: err?.error?.message ? [err.error.message] : null,
          },
        }, err.status);
      },
    });
  }

  openJasoner(e) {
    e.stopPropagation(); // don't close menu
    this.jasonerLinkLoading.set(true);

    this.apiService.getJasonerLink()
        .pipe(takeUntil(this.componentDestroyed))
        .subscribe({
          next: res => {
            this.jasonerLinkLoading.set(false);
            this.closeAccountSettingsMenu();
            window.open(res.message);
          },
          error: (err: HttpErrorResponse) => {
            console.error(err);
            this.jasonerLinkLoading.set(false);
            this.feedbackService.showAlertSnackbar({
              data: {
                userFriendlyMessage: this.translate.instant('ERROR'),
                detailedInfoFromBE: err?.error?.message ? [err.error.message] : null,
              },
            }, err.status);
          },
        });
  }


}
